import { designVariables } from "@studio3t/design-variables";
import {
  ButtonSmallSubtleIcon,
  GenericDropdown,
  GenericDropdownContext,
  Icon,
  IconSVG,
  type ListItemProps,
  SVGColorKey,
} from "@studio3t/generic-components";
import type { ReactNode } from "react";
import styled from "styled-components";
import {
  DROPDOWN_TRIGGER_ATTR,
  DropdownOpenReporter,
} from "@/components/dropdownDismissGuard";

const SINGLE_LINE = designVariables.type.singleLine;
const SEMANTIC = designVariables.ui.semantic;
const BUTTON = designVariables.ui.button;
const TABLE = designVariables.ui.table;

export const DEFAULT_PAGE_SIZES = [10, 25, 50, 100];

const PaginationBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${SEMANTIC.spacings}px;
  padding: ${TABLE.padding}px;
  border-top: 1px solid ${(props) => props.theme.tableBackground};
`;

const RangeText = styled.span`
  font-size: ${SINGLE_LINE.small.regular.fontSize}px;
  color: ${(props) => props.theme.semanticTextBody};
  white-space: nowrap;
`;

// GenericDropdown's root div is display:block and would stretch to the
// remaining row width, so shrink-wrap it to its trigger.
const PageSizeWrap = styled.div`
  flex: 0 0 auto;
`;

// Claims the bar's left side; margin-right pushes the paging controls back
// to the right edge they normally hug.
const LeadingSlot = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
  margin-right: auto;
`;

// Secondary-button-style dropdown trigger. Built locally rather than via the
// prebuilt button derivative because Figma uses a small 10px BaseChevron
// (Figma var Button/chevron — not exported by design-variables yet), while
// the derivative renders icons at the fixed 16px button icon size.
const PageSizeTrigger = styled.button<{ $active?: boolean }>`
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  gap: ${BUTTON.gap}px;
  height: ${BUTTON.s.size}px;
  padding: 0 ${BUTTON.s.paddingw}px;
  border-radius: ${BUTTON.corner}px;
  font-size: ${SINGLE_LINE.small.medium.fontSize}px;
  font-weight: ${SINGLE_LINE.small.medium.fontWeight};
  line-height: 1.2;
  white-space: nowrap;
  cursor: pointer;
  color: ${(props) => props.theme.buttonSecondaryDefaultText};
  background: ${(props) =>
    props.$active
      ? props.theme.buttonSecondaryActiveBackground
      : props.theme.buttonSecondaryDefaultBackground};
  border: 1px solid
    ${(props) =>
      props.$active
        ? props.theme.buttonSecondaryActiveBorder
        : props.theme.buttonSecondaryDefaultBorder};
  transition:
    background 0.15s ease,
    border-color 0.15s ease;

  &:hover {
    background: ${(props) => props.theme.buttonSecondaryHoverBackground};
    border-color: ${(props) => props.theme.buttonSecondaryHoverBorder};
  }
`;

function PageSizeSelect({
  pageSize,
  pageSizes,
  onChange,
  onOpenChange,
}: {
  pageSize: number;
  pageSizes: number[];
  onChange: (size: number) => void;
  onOpenChange?: (open: boolean) => void;
}) {
  return (
    <GenericDropdown
      width={{ strategy: "fixed", size: 90 }}
      placement="bottom-end"
      dropdownItems={[
        pageSizes.map(
          (n): ListItemProps => ({
            id: String(n),
            type: "text",
            label: String(n),
            value: String(n),
            closeOnSelect: true,
            selected: { value: n === pageSize, icon: "dot" },
            onClick: () => onChange(n),
          }),
        ),
      ]}
    >
      <GenericDropdownContext.Consumer>
        {({ open }) => (
          <PageSizeTrigger
            type="button"
            $active={open}
            {...DROPDOWN_TRIGGER_ATTR}
          >
            <DropdownOpenReporter onOpenChange={onOpenChange} />
            {String(pageSize)}
            <IconSVG
              icon={Icon.BaseChevron}
              size={10}
              svgColorKey={SVGColorKey.semanticTextBody}
            />
          </PageSizeTrigger>
        )}
      </GenericDropdownContext.Consumer>
    </GenericDropdown>
  );
}

// Presentational pagination bar shared by the Pipelines list and the Workers
// table. It owns no page state — the caller passes the current index/size and
// the clamped page count, and is responsible for resetting to page 0 on a size
// change. The bar defensively clamps the displayed range/disabled state so a
// momentarily stale index (e.g. data shrank under a poll) never shows a
// negative range.
// GenericButton forwards no aria-label or title; the node gets them.
const labelled = (label: string) => (el: HTMLButtonElement | null) => {
  el?.setAttribute("aria-label", label);
  el?.setAttribute("title", label);
};

export function TablePagination({
  pageIndex,
  pageCount,
  pageSize,
  total,
  pageSizes = DEFAULT_PAGE_SIZES,
  onPageChange,
  onPageSizeChange,
  onOpenChange,
  leading,
}: {
  pageIndex: number;
  pageCount: number;
  pageSize: number;
  total: number;
  pageSizes?: number[];
  /** The page-size menu's open state, for a table's dropdownDismissGuard. */
  onOpenChange?: (open: boolean) => void;
  onPageChange: (index: number) => void;
  onPageSizeChange: (size: number) => void;
  /** Optional content pinned to the bar's left edge (e.g. the Audit log's
   *  "Export filtered results" button — Figma 13759-112389); the paging
   *  controls keep hugging the right. */
  leading?: ReactNode;
}) {
  const safePage = Math.min(pageIndex, Math.max(0, pageCount - 1));
  const rangeStart = total === 0 ? 0 : safePage * pageSize + 1;
  const rangeEnd = Math.min((safePage + 1) * pageSize, total);

  return (
    <PaginationBar>
      {leading != null && <LeadingSlot>{leading}</LeadingSlot>}
      {/* The library's icon buttons drop aria props, so their names — the
          only text a keyboard or screen-reader user gets — go on the node
          (TTTL-186). */}
      <ButtonSmallSubtleIcon
        ref={labelled("First page")}
        icon={Icon.LeftStop}
        onClick={() => onPageChange(0)}
        disabled={safePage === 0}
      />
      <ButtonSmallSubtleIcon
        ref={labelled("Previous page")}
        icon={Icon.Left}
        onClick={() => onPageChange(Math.max(0, safePage - 1))}
        disabled={safePage === 0}
      />
      <RangeText>
        {rangeStart} to {rangeEnd}
      </RangeText>
      <PageSizeWrap>
        <PageSizeSelect
          pageSize={pageSize}
          pageSizes={pageSizes}
          onChange={onPageSizeChange}
          onOpenChange={onOpenChange}
        />
      </PageSizeWrap>
      <ButtonSmallSubtleIcon
        ref={labelled("Next page")}
        icon={Icon.Right}
        onClick={() =>
          onPageChange(Math.max(0, Math.min(pageCount - 1, safePage + 1)))
        }
        disabled={safePage >= pageCount - 1}
      />
      <ButtonSmallSubtleIcon
        ref={labelled("Last page")}
        icon={Icon.RightStop}
        onClick={() => onPageChange(Math.max(0, pageCount - 1))}
        disabled={safePage >= pageCount - 1}
      />
    </PaginationBar>
  );
}
